import { WORKSPACE_ADMIN } from '../../constants/roles'

export const canAccessStoryMapMigration = (userProfile) => {
    if (!userProfile) return false

    if (userProfile.isSupportUser && userProfile.isSuperAdmin) {
        return true
    }

    if (userProfile?.role?.roleName === WORKSPACE_ADMIN) {
        return true
    }

    return false
}

export const MIGRATION_COUNT_LABELS = {
    storyMap: 'Story maps',
    journey: 'Journeys',
    step: 'Steps',
    release: 'Releases',
    story: 'Stories',
    comment: 'Comments',
    activity: 'Activity',
    shareLink: 'Share links',
}

export const MIGRATION_MISSING_REF_LABELS = {
    step: 'Steps',
    story: 'Stories',
    comment: 'Comments',
    activity: 'Activity',
}

export const MIGRATION_STATUS_META = {
    empty: {
        label: 'No story map data',
        tone: 'neutral',
        canMigrate: false,
    },
    none: {
        label: 'Not migrated',
        tone: 'info',
        canMigrate: true,
    },
    partial: {
        label: 'Partially migrated',
        tone: 'warning',
        canMigrate: true,
    },
    complete: {
        label: 'Fully migrated',
        tone: 'success',
        canMigrate: false,
    },
}

export const MIGRATION_RESULT_META = {
    success: {
        label: 'Success',
        tone: 'success',
    },
    partial: {
        label: 'Partial success',
        tone: 'warning',
    },
    already_migrated: {
        label: 'Already migrated',
        tone: 'neutral',
    },
    nothing_to_migrate: {
        label: 'Nothing to migrate',
        tone: 'neutral',
    },
}

export const MIGRATION_MESSAGE_LABELS = {
    MIGRATION_PREVIEW_READY: 'Preview ready — new data will be copied.',
    MIGRATION_PREVIEW_PARTIAL: 'Preview ready — some items already migrated, some new.',
    MIGRATION_SUCCESS: 'All pending data was migrated successfully.',
    MIGRATION_PARTIAL_SUCCESS: 'Some data migrated; some items were already migrated.',
    MIGRATION_ALREADY_DONE: 'Everything was already migrated — no new data copied.',
    MIGRATION_NOTHING_TO_MIGRATE: 'Source project has no story map data to migrate.',
}

export const getMigrationStatusMeta = (status) =>
    MIGRATION_STATUS_META[status] ?? MIGRATION_STATUS_META.none

export const getMigrationResultMeta = (migrationResult) =>
    MIGRATION_RESULT_META[migrationResult] ?? null

export const getMigrationMessageLabel = (messageKey, fallback = '') =>
    MIGRATION_MESSAGE_LABELS[messageKey] || fallback

export const resolveMigrationDisplay = ({
    pairStatus,
    selectedSource,
    selectedTargetId,
} = {}) => {
    const isPairScoped = Boolean(selectedSource && selectedTargetId && pairStatus)

    if (isPairScoped) {
        return {
            isPairScoped: true,
            migrationStatus: pairStatus.migrationStatus,
            pendingStoryMapCount: pairStatus.pendingStoryMapCount,
            migratedStoryMapCount: pairStatus.migratedStoryMapCount,
            migratedTo: pairStatus.migratedTo ?? null,
            canMigrate: pairStatus.canMigrate,
        }
    }

    return {
        isPairScoped: false,
        migrationStatus: selectedSource?.migrationStatus,
        pendingStoryMapCount: selectedSource?.pendingStoryMapCount,
        migratedStoryMapCount: selectedSource?.migratedStoryMapCount,
        migratedTo: selectedSource?.migratedTo ?? null,
        canMigrate: selectedSource?.canMigrate,
    }
}

export const resolveCanMigrate = ({
    pairStatus,
    selectedSource,
    selectedTargetId,
} = {}) => {
    // When a target is selected, only the pair /status response is authoritative.
    // Source-level "complete" may mean migrated to a different PMS project.
    if (selectedTargetId) {
        if (pairStatus && typeof pairStatus.canMigrate === 'boolean') {
            return pairStatus.canMigrate
        }
        return false
    }

    if (selectedSource?.migrationStatus === 'empty') return false

    if (typeof selectedSource?.canMigrate === 'boolean') {
        return selectedSource.canMigrate
    }

    return Boolean(selectedSource?.pendingStoryMapCount > 0)
}

export const hasMissingRefs = (missingRefs = {}) =>
    Object.values(missingRefs).some((count) => Number(count) > 0)

export const shouldNavigateAfterMove = (result) => {
    if (!result || result.dryRun) return false

    if (result.migrationResult === 'already_migrated') return false
    if (result.migrationResult === 'nothing_to_migrate') return false
    if (result.hasNewData === false) return false

    return (
        result.migrationResult === 'success' ||
        result.migrationResult === 'partial' ||
        (result.totalCreated ?? 0) > 0
    )
}

export const getMigrationToastType = (result) => {
    if (!result) return 'info'

    if (
        result.migrationResult === 'already_migrated' ||
        result.migrationResult === 'nothing_to_migrate'
    ) {
        return 'info'
    }

    if (result.migrationResult === 'partial') return 'warning'

    return 'success'
}
